import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { InterfaceEmployee } from 'app/models/employee.model';
import { EmployeeService } from 'app/services/employee.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { timezone } from 'assets/constants/timezone';
import { take } from 'rxjs/operators';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezoneDayjs from 'dayjs/plugin/timezone';
import { InterfaceJob } from 'app/models/job.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
dayjs.extend(utc);
dayjs.extend(timezoneDayjs);

@Component({
  selector: 'app-califications',
  templateUrl: './califications.component.html',
  styleUrls: ['./califications.component.scss']
})
export class CalificationsComponent implements OnInit, OnDestroy {

  @Input() user: InterfaceEmployee;
  calification: any;
  gaugeChartOptions = {
    min: 0,
    max: 100,
    greenFrom: 85,
    greenTo: 100,
    redFrom: 0,
    redTo: 50,
    yellowFrom: 50,
    yellowTo: 85,
    minorTicks: 25
  };
  actualYear: number;
  actualMonth: number;
  jobsInCharge: InterfaceJob[] = [];
  employeesInCharge: any = {};
  calificationsSupervisor: any = {};
  supervisors: InterfaceEmployee[] = [];
  calificationsAuditor: any = {};
  calificationPeriod: FormGroup;
  subscriptions: Subscription[] = [];

  constructor(
    protected employeeService: EmployeeService,
    private spinner: NgxSpinnerService
  ) {
    this.actualYear = dayjs().tz(timezone).get("year");
    this.actualMonth = dayjs().tz(timezone).month() + 1;
    this.calificationPeriod = new FormGroup({
      year: new FormControl(this.actualYear, Validators.required),
      month: new FormControl(this.actualMonth, Validators.required)
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.calificationPeriod.controls.year.valueChanges.subscribe((value: number) => {
        if (value) {
          this.actualYear = value;
          this.calificationPeriod.controls.month.patchValue(0, { emitEvent: false });
          this.calificationPeriod.controls.month.updateValueAndValidity({ emitEvent: false });
        }
      }, (errorControl: any) => {
        console.error(errorControl);
      })
    );
    this.subscriptions.push(
      this.calificationPeriod.controls.month.valueChanges.subscribe((value: number) => {
        if (value) {
          this.actualMonth = value;
          this.reset();
        }
      }, (errorControl: any) => {
        console.error(errorControl);
      })
    );
    this.reset();
  }

  async reset(): Promise<void> {
    this.spinner.show(undefined,
      {
        type: 'square-jelly-box',
        size: 'medium',
        bdColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        fullScreen: true
      });
    this.user = await this.employeeService.getCurrentUser();
    this.getMyCalification();
    this.getSupervisorCalifications();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }

  getMyCalification(): void {
    this.employeeService.getMyCalification(this.user.id, this.actualYear, this.actualMonth).pipe(take(1)).subscribe((result: any) => {
      if (result.length > 0) {
        this.calification = result[0];
      } else {
        this.calification = {
          calification: 0,
          checkPointsDone: 0,
          mustHaveCheckPointsDone: 0
        }
      }
      this.spinner.hide();
    }, (errorGetMyCalification: any) => {
      console.error(errorGetMyCalification);
      this.spinner.hide();
    });
  }

  getSupervisorCalifications(): void {
    if (this.user.role === "supervisor") {
      this.employeeService.getJobsSupervisor(this.user.id).pipe(take(1)).subscribe((result: any) => {
        this.jobsInCharge = result;
        for (let i = 0; i < this.jobsInCharge.length; i++) {
          this.getEmployeesOfJob(this.jobsInCharge[i].id);
        }
      }, (errorSupervisorJobs) => {
        console.error(errorSupervisorJobs);
      });
    } else if (this.user.role === "auditor") {
      this.employeeService.getJobsAuditor(this.user.id).pipe(take(1)).subscribe((result: any) => {
        this.jobsInCharge = result;
        for (let i = 0; i < this.jobsInCharge.length; i++) {
          this.getEmployeesOfJob(this.jobsInCharge[i].id);
          this.getSupervisorOfJob(this.jobsInCharge[i]);
        }
      }, (errorSupervisorJobs: any) => {
        console.error(errorSupervisorJobs);
      });
    }
  }

  getEmployeesOfJob(jobId: string): void {
    this.employeeService.getEmployeesByJob(jobId).pipe(take(1)).subscribe((result: any) => {
      this.employeesInCharge[jobId] = result;
      for (let i = 0; i < this.employeesInCharge[jobId].length; i++) {
        this.getCalificationsOfEmployees(this.employeesInCharge[jobId][i].id);
      }
    }, (errorGetEmployeesOfJob: any) => {
      console.error(errorGetEmployeesOfJob);
    });
  }

  getSupervisorOfJob(job: InterfaceJob): void {
    this.employeeService.getSupervisorOfJob(job).pipe(take(1)).subscribe((result: any) => {
      this.supervisors = result;
      for (let i = 0; i < this.supervisors.length; i++) {
        this.getCalificationsOfSupervisors(this.supervisors[i].id);
      }
    }, (errorGetSupervisor: any) => {
      console.error(errorGetSupervisor);
    });
  }

  getCalificationsOfSupervisors(supervisorId: string): void {
    this.employeeService.getMyCalification(supervisorId, this.actualYear, this.actualMonth).pipe(take(1)).subscribe((result: any) => {
      if (result.length > 0) {
        this.calificationsAuditor[supervisorId] = result[0];
      } else {
        this.calificationsAuditor[supervisorId] = {
          calification: 0,
          checkPointsDone: 0,
          mustHaveCheckPointsDone: 0
        }
      }
    }, (errorGetEmployeeCalification: any) => {
      console.error(errorGetEmployeeCalification);
    });
  }

  getCalificationsOfEmployees(employeeId: string): void {
    this.employeeService.getMyCalification(employeeId, this.actualYear, this.actualMonth).pipe(take(1)).subscribe((result: any) => {
      if (result.length > 0) {
        this.calificationsSupervisor[employeeId] = result[0];
      } else {
        this.calificationsSupervisor[employeeId] = {
          calification: 0,
          checkPointsDone: 0,
          mustHaveCheckPointsDone: 0
        }
      }
    }, (errorGetEmployeeCalification: any) => {
      console.error(errorGetEmployeeCalification);
    });
  }

  returnMonthName(): string {
    switch (this.actualMonth) {
      case 1:
        return "Enero";
      case 2:
        return "Febrero";
      case 3:
        return "Marzo";
      case 4:
        return "Abril";
      case 5:
        return "Mayo";
      case 6:
        return "Junio";
      case 7:
        return "Julio";
      case 8:
        return "Agosto";
      case 9:
        return "Septiembre";
      case 10:
        return "Octubre";
      case 11:
        return "Noviembre";
      case 12:
        return "Diciembre";
      default:
        return "Not found";
    }
  }

}
